/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useMemo } from 'react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ResourceColumn,
  RESOURCE_LIST_COLUMNS,
  RESOURCE_LIST_DEFAULT_COLUMNS,
  ResourceListProps,
} from '../types';

import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

import { ResourceIndexView, ResourceType } from '@app/types/blackroom-api';
import CellResource from '@features/Resource/ResourceTable/cells/CellResource';
import { useDateFormatter } from '@app/shared/helpers/hooks/useDateFormatter';
import { ResourceScope } from '@app/types/blackroom-app';

/* -------------------------------------------------------------------------- */
/*                               Implementation                               */
/* -------------------------------------------------------------------------- */

export default function useColumns({
  columns = RESOURCE_LIST_DEFAULT_COLUMNS,
  scope = 'my',
  listProps,
}: {
  columns?: ResourceColumn[];
  scope?: ResourceScope;
  listProps?: ResourceListProps;
} = {}) {
  const { t } = useTranslation('Resource');
  const workspace = useWorkspace();
  const { formatDate } = useDateFormatter();

  const columnDefs = useMemo<MRT_ColumnDef<ResourceIndexView>[]>(
    () =>
      columns
        .filter((f) => RESOURCE_LIST_COLUMNS.includes(f as ResourceColumn))
        .map((col) => {
          const sharedColDef: MRT_ColumnDef<ResourceIndexView> = {
            accessorKey: col,
            enableSorting: false,
            enableColumnActions: false,
            enableColumnFilter: false,
            grow: false,
            header: t(`ResourceTable.columns.${col}.label`),
          };
          switch (col) {
            case 'resource':
              return {
                ...sharedColDef,
                minSize: 300,
                grow: true,
                Cell: (props) => {
                  const inner = <CellResource<ResourceIndexView> {...props} />;
                  const resource = props.row.original;

                  const basePath = `/workspace/${workspace.id}`;
                  if (listProps?.withNavigation) {
                    return (
                      <Link
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        to={
                          resource.type === ResourceType.Folder
                            ? `${basePath}/resources/${scope}/${resource.id}`
                            : `${basePath}/file/${resource.id}`
                        }
                        target={
                          resource.type === ResourceType.File
                            ? '_blank'
                            : undefined
                        }
                        style={{ width: '100%' }}
                      >
                        {inner}
                      </Link>
                    );
                  }
                  return inner;
                },
                enableGlobalFilter: true,
                accessorFn: (row) => row.name,
              };

            case 'index':
              return {
                ...sharedColDef,
                size: 110,
              };

            case 'createdAt':
              return {
                ...sharedColDef,
                accessorFn: (row) => formatDate(row.createdAt),
              };

            default:
              break;
          }
          return sharedColDef;
        }),
    [workspace, t],
  );

  return columnDefs;
}
